import { AfterContentChecked, Component, ElementRef, ErrorHandler, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { parseJSON } from 'date-fns';
import { catchError, map, tap } from 'rxjs/operators';
import { WebSocketSubject } from 'rxjs/webSocket';
import { BetService } from 'src/app/bet.service';
import { BlockConfigurationObject } from 'src/app/block-configuration-object.model';
import { GeneralService } from 'src/app/general.service';
import { LocalisationService } from 'src/app/localisation.service';
import { MatchLiveData } from 'src/app/match-live-data.model';
import { IsLivePipe } from 'src/app/match-pipes/is-live.pipe';
import { Match } from 'src/app/match.model';
import { MatchService } from 'src/app/match.service';
import { SportDataService } from 'src/app/sport-data.service';
import { environment } from 'src/environments/environment';

@Component({
	selector: "app-sidebar-block",
	templateUrl: "./sidebar-block.component.html",
	styleUrls: ["./sidebar-block.component.scss"],
	encapsulation: ViewEncapsulation.None
})
export class SidebarBlockComponent implements OnInit, OnDestroy, OnChanges, AfterContentChecked {

	@Input() configurationObject: BlockConfigurationObject;
	@ViewChild("blockBody") blockBody: ElementRef;

	displayMaximumWidths = {
		compact: 350,
		"less-compact": 600,
		medium: 700,
		large: 800,
	};
	displayMode = "";
	gamesError = false;
	gamesLoading = true;
	liveDataTimeout = null;
	liveToggleState = "next";
	matches: Array<Match> = [];
	sidebarSports: Array<string> = [];
	selectedSport = "";
	blockInitStarted = false;
	displayedMatches: Array<Match> = [];
	sidebarSportOptions: Array<string> = [];
	liveSocketError = false;
	liveDataSocket$: WebSocketSubject<any>;

	/* @ngInject */
	constructor(
		private errorHandler: ErrorHandler,
		public matchService: MatchService,
		public sportDataService: SportDataService,
		public generalService: GeneralService,
		public betService: BetService,
		private el: ElementRef,
		public localisationService: LocalisationService,
		private isLivePipe: IsLivePipe,
	) { }

	ngOnInit(): void {
		if (this.configurationObject && !this.blockInitStarted) {
			this.prepareBlock();
		}

	}

	ngOnDestroy(): void {
		if (this.liveDataTimeout) {
			// console.log('timeout called')
			clearTimeout(this.liveDataTimeout);
			this.liveDataTimeout = null;
		}

		// close match data socket
		if (this.liveDataSocket$ && !this.liveDataSocket$.closed) {
			this.liveDataSocket$.complete();
		}

	}

	prepareBlock(): void {
		this.blockInitStarted = true;
		this.generalService.initialiseBlock(this.el);
		this.sidebarSports = this.configurationObject.sports.map(s => s.toUpperCase());
		if (typeof window !== "undefined") {
			window.addEventListener("resize", this.checkCompact.bind(this));
		}

		if (typeof IntersectionObserver !== "undefined") {
			let elementShown = false;

			new IntersectionObserver((entries) => {
				entries.forEach(e => {
					if (e.intersectionRatio > 0 && !elementShown) {
						elementShown = true;
						this.matchService.getGeoData().then(() => {
							this.retrieveGames();
						}, () => {
							this.retrieveGames();
						});
					}
				})
			}, {
				rootMargin: "50px 0px 0px 0px",
			}).observe(this.el.nativeElement);
		} else {
			this.matchService.getGeoData().then(() => {
				this.retrieveGames();
			}, () => {
				this.retrieveGames();
			});
		}

		
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!this.blockInitStarted && changes.configurationObject.currentValue) {
			this.prepareBlock();
		} else if (changes.configurationObject.currentValue && changes.configurationObject.previousValue
			&& changes.configurationObject.currentValue.sports !== changes.configurationObject.previousValue.sports) {
				this.prepareBlock();
		}

		if (changes.selectedSport && changes.selectedSport.currentValue !== changes.selectedSport.previousValue) {
			this.handleSportChange();
		}
	}

	setSidebarSportOptions(): void {
		this.sidebarSportOptions = this.sidebarSports.filter((sport: string) => this.matches
			.some(this.matchService.matchFitsFilter(sport)));
	}

	handleSportChange(): void {
		// if user was on live mode, and then goes to sport with no live games, snap back to Next mode
		if (this.selectedSport !== "" && this.configurationObject.showLiveToggle && !this.anyLiveMatches()) {
			this.liveToggleState = "next";
		}

		this.setDisplayedMatches();
	}

	checkCompact(): void {
		let modeString = "full";
		const element = this.blockBody.nativeElement;
		if (element) {
			Object.keys(this.displayMaximumWidths).forEach(level => {
				if (element && element.offsetWidth <= this.displayMaximumWidths[level]) {
					modeString += ` ${level}`;
				}
			});
		}


		this.displayMode = modeString;
	}

	ngAfterContentChecked(): void {
		const element = this.el?.nativeElement;
		if (element && !this.displayMode) {
			this.displayMode = "full";
			Object.keys(this.displayMaximumWidths).forEach(level => {
				if (element && element.offsetWidth <= this.displayMaximumWidths[level]) {
					this.displayMode += ` ${level}`;
				}
			});
			// const blockParent = element.parentNode;
			// if (blockParent && typeof MutationObserver !== "undefined") {
			// 	new MutationObserver(() => {
			// 		if (this.el.nativeElement === null) {
			// 			if (this.liveDataTimeout) {
			// 				clearTimeout(this.liveDataTimeout);
			// 				this.liveDataTimeout = null;
			// 			}

			// 			// close match data socket
			// 			if (this.liveDataSocket$ && !this.liveDataSocket$.closed) {
			// 				this.liveDataSocket$.complete();
			// 			}
			// 		}
			// 	}).observe(blockParent, {childList: true, subtree: true});
			// }
		}
	}


	checkForNewMatches(): void {
		let bookmakerList: Array<string> = [];
		bookmakerList = environment.bookmakers;
		this.sportDataService.getUpcomingGames(this.sidebarSports, true, true, bookmakerList, environment.sportExclusiveBookmakers)
			.subscribe((matches) => {
				const existingMatchList: Match[] = this.matches.slice();
				const newMatchList: Match[] = matches.filter(g => this.sidebarSports.includes(g.MatchData.Sport.toUpperCase()));

				newMatchList.forEach(match => {
					if (!existingMatchList.some(m => m.MatchData.SIMatchID === match.MatchData.SIMatchID)) {
						match.MatchData.UserDate = parseJSON(match.MatchData.Date);
						this.betService.populateTileBettingInfo(match);
						existingMatchList.push(match);
					}
				});

				existingMatchList.sort((a,b) => this.matchService.gameDisplaySort(a,b));

				this.matches = existingMatchList;

				this.setDisplayedMatches();

				this.setSidebarSportOptions();

				// if the current selected sport is no longer available because its games have dropped off,
				// revert to showing All games (or if that's not an option, the first sport available on the list)
				if (this.selectedSport !== "all" && !existingMatchList.some(m => m.MatchData.Sport.toLowerCase() === this.selectedSport)
					&& this.configurationObject.showAllOption) {
					this.selectedSport = "all";
				} else if (this.selectedSport !== "all"
					&& !existingMatchList.some(m => m.MatchData.Sport.toLowerCase() === this.selectedSport)
					&& this.sidebarSportOptions.length > 0) {
					this.selectedSport = this.sidebarSportOptions[0].toLowerCase();
				}

				if (this.configurationObject.showLiveToggle && this.liveToggleState === "live"
					&& !existingMatchList.some(m => this.isLivePipe.transform(m))) {
					this.liveToggleState = "next";
				}

				if (typeof window !== "undefined") {
					// console.log('timeout called')
					this.liveDataTimeout = setTimeout(() => this.checkForNewMatches(), 60000);
				}
			}, (err) => {
				if (err !== "API_CALL_FAILED") {
					this.errorHandler.handleError(err);
					// console.error(err);
				}
			});
	}

	retrieveGames(): void {
		this.gamesLoading = true;
		this.gamesError = false;

		let bookmakerList: Array<string> = [];
		bookmakerList = environment.bookmakers;

		this.sportDataService.getUpcomingGames(this.sidebarSports, true, true, bookmakerList, environment.sportExclusiveBookmakers)
			.subscribe((matches) => {
				this.matches = matches.filter(g => this.sidebarSports.includes(g.MatchData.Sport.toUpperCase()));

				this.matches.forEach((match: Match) => {
					match.MatchData.UserDate = parseJSON(match.MatchData.Date);
					this.betService.populateTileBettingInfo(match);
					match.homeScoreJustChanged = false;
					match.awayScoreJustChanged = false;
				});

				this.matches.sort((a: Match,b: Match) => this.matchService.gameDisplaySort(a,b));

				this.setSidebarSportOptions();

				if (this.configurationObject.showAllOption) {
					this.selectedSport = "all";
				} else if (this.sidebarSportOptions.length > 0) {
					this.selectedSport = this.sidebarSportOptions[0].toLowerCase();
				}

				if (this.configurationObject.showLiveToggle && this.liveToggleState === "live"
					&& !this.matches.some(m => this.isLivePipe.transform(m))) {
					this.liveToggleState = "next";
				}

				this.setDisplayedMatches();

				this.gamesLoading = false;

				if (typeof window !== "undefined") {
					// console.log('timeout called')
					this.liveDataTimeout = setTimeout(() => this.checkForNewMatches(), 60000);
				}

				this.connectToLiveStream();
			}, (err) => {
				if (err !== "API_CALL_FAILED") {
					this.errorHandler.handleError(err);
					// console.error(err);
				}
				this.gamesError = true;
			});
	}

	updateLiveData(newRow: MatchLiveData): void {
		// console.log("[Sidebar Block Socket] New row arrived for " + newRow.SIMatchID + ":");
		// console.log(newRow);
		if (newRow.stat_type !== "detailed") {
			const existingMatchList: Match[] = this.matches.slice();

			if (existingMatchList.some(m => m.MatchData.SIMatchID === newRow.SIMatchID)) {
				const oldMatchData = existingMatchList.find(m => m.MatchData.SIMatchID === newRow.SIMatchID);
				if (oldMatchData.LiveData !== newRow) {
					if (oldMatchData.LiveData && newRow && newRow.sr_event_sequence >= oldMatchData.LiveData.sr_event_sequence) {
						if (oldMatchData.LiveData.homeScore !== newRow.homeScore) {
							oldMatchData.homeScoreJustChanged = true;
							// console.log('timeout called')
							setTimeout(() => oldMatchData.homeScoreJustChanged = false, 1000);
						}

						if (oldMatchData.LiveData.awayScore !== newRow.awayScore) {
							oldMatchData.awayScoreJustChanged = true;
							// console.log('timeout called')
							setTimeout(() => oldMatchData.awayScoreJustChanged = false, 1000);
						}

					}

					oldMatchData.LiveData = newRow;
				}
			}

			existingMatchList.sort((a,b) => this.matchService.gameDisplaySort(a,b));

			this.matches = existingMatchList;

			this.setSidebarSportOptions();

			this.setDisplayedMatches();

			// if the current selected sport is no longer available because its games have dropped off, revert to showing All games
			if (this.selectedSport !== "all" && !existingMatchList.some(m => m.MatchData.Sport.toLowerCase() === this.selectedSport)) {
				this.selectedSport = "all";
			}
		}
	}

	reloadGames(): void {
		Promise.resolve(true).then(() => {
			this.retrieveGames();
		});
	}

	setLiveToggleState(state: string): void {
		this.liveToggleState = state;
		this.setDisplayedMatches();
	}

	fitsLiveToggle(match: Match): boolean {
		if (!this.configurationObject.showLiveToggle) {
			return true;
		}

		if (this.liveToggleState === "live" && this.isLivePipe.transform(match)) {
			return true;
		}

		if (this.liveToggleState === "next" && this.matchService.isUpcoming(match)) {
			return true;
		}

		return false;
	}

	anyLiveMatches(): boolean {
		return this.matches
			.some(m => this.matchService.matchFitsFilter(this.selectedSport)(m)
				&& this.isLivePipe.transform(m));
	}

	setDisplayedMatches(): void {
		this.displayedMatches = this.matches.filter(m =>
			this.fitsLiveToggle(m) && this.matchService.matchFitsFilter(this.selectedSport)(m)
		).sort((a,b) => this.matchService.gameDisplaySort(a,b)).slice(0,4);
	}

	connectToLiveStream = () => {
		this.liveDataSocket$ = this.sportDataService.connectToUpcomingPushStream(this.sidebarSports);
		console.log("About to connect to socket (Sidebar Block)");
		const liveData$ = this.liveDataSocket$.pipe(
			map((row: MatchLiveData) => {
				this.updateLiveData(row);
				return row;
			}),
			tap({
				error: error => {
					if (error?.type === "close") {
						console.log("[Sidebar Block Socket] Connection Closed");
						this.liveSocketError = true;
					} else {
						this.errorHandler.handleError("[Sidebar Block Socket] Error:" +
							JSON.stringify(error, ["message", "arguments", "type", "name"]))
					};
				},
				complete: () => {
					console.log("[Sidebar Block Socket] Connection Closed");
					this.liveSocketError = true;
				}
			})
		);

		liveData$.subscribe();
	};

}
